<template>
  <main
    v-if="data && generalData"
    class="text-white">
    <div class="bg-gray-500 pb-12 lg:pb-24">
      <JobHeader />

      <Container class="flex flex-wrap items-start justify-between space-x-6 lg:flex-nowrap">
        <div>
          <h1 class="max-w-2xl whitespace-pre-line text-5xl tracking-tight text-white lg:text-7xl lg:leading-tight">
            {{ data.title }}
          </h1>

          <JobSidebar
            :content="sidebarContent"
            :cta="generalData.sidebar.cta"
            class="mt-12 block flex-shrink-0 lg:hidden"
            @button-click="handleClick" />

          <article class="mt-12 rounded-3xl bg-white px-6 py-6 lg:px-8 lg:py-12">
            <div
              class="prose prose-lg text-base font-normal text-gray-400 lg:text-lg"
              v-html="data.content" />
          </article>
        </div>

        <JobSidebar
          :content="sidebarContent"
          :cta="generalData.sidebar.cta"
          class="top-24 hidden flex-shrink-0 lg:sticky lg:block"
          @button-click="handleClick" />
      </Container>
    </div>

    <JobModalPublic
      :id="entryId"
      :is-open="isModalOpen"
      @toggle="setIsOpen" />
  </main>
</template>

<script setup lang="ts">
  import { onMounted, ref } from 'vue'
  import { useRoute, useRouter } from 'vue-router'

  import routesList from '~/content/routes.json'
  import type { Prospect } from '~/models/jobs'
  import { getFirstQueryValue } from '~/utils/query'
  import { stripEndingSlash } from '~/utils/urls'

  const { $event, $lenisMount } = useNuxtApp()
  const route = useRoute()
  const router = useRouter()
  const { data: generalData } = await useAsyncData('jobs-general', () => queryContent('globals', 'jobs').findOne())
  const isModalOpen = ref(false)

  const uri = stripEndingSlash(route.path)
  const page = routesList.find(item => item.collection === 'prospects' && item.url === uri)

  if (!page) {
    throw createError({
      statusCode: 404,
      statusMessage: 'Page Not Found',
      fatal: true
    })
  }

  const entryId = page.id
  const { data } = await useAsyncData(entryId, () =>
    queryContent<Prospect>('prospects').where({ id: entryId }).findOne()
  )
  const sidebarContent = {
    locations: { title: generalData.value.sidebar.locations, items: data.value.locations }
  }

  useHead({
    title: data.value?.meta.title,
    link: [
      {
        rel: 'canonical',
        href: `https://outfit7.com${uri}`
      }
    ],
    meta: [
      { hid: 'description', name: 'description', content: data.value?.meta.description },
      { hid: 'og:title', property: 'og:title', content: data.value?.social.title },
      { hid: 'og:description', property: 'og:description', content: data.value?.social.description },
      { hid: 'twitter:title', name: 'twitter:title', content: data.value?.social.title },
      { hid: 'twitter:description', name: 'twitter:description', content: data.value?.social.description }
    ]
  })

  onMounted(() => {
    $lenisMount()
  })

  function setIsOpen(state: boolean) {
    isModalOpen.value = state
    syncGreenhouseJobQueryParam(state).catch(console.error)

    if (!data.value) {
      return
    }

    $event('Job Application', {
      props: { [state ? 'Open' : 'Close']: data.value.title }
    })
  }

  function handleClick() {
    setIsOpen(true)
  }

  // Keep the URL's greenhouse job query param aligned with the modal state so
  // opening a job sets `gh_jid` for deep links and closing it removes the param.
  async function syncGreenhouseJobQueryParam(isOpen: boolean) {
    const currentGreenhouseJobId = getFirstQueryValue(route.query.gh_jid)

    if ((isOpen && currentGreenhouseJobId === entryId) || (!isOpen && !currentGreenhouseJobId)) {
      return
    }

    const query = { ...route.query }

    if (isOpen) {
      query.gh_jid = entryId
    } else {
      delete query.gh_jid
    }

    await router.replace({
      path: route.path,
      query,
      hash: route.hash
    })
  }
</script>
